import 'dayjs/locale/es';
import 'dayjs/locale/en';
import { TextField, TextFieldProps } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { Controller, ControllerProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import type { CSSProperties } from 'react';

type DatePickerTranslatedProps<T> = {
  name: string;
  control: any;
  inputProps?: TextFieldProps['inputProps'];
  size?: TextFieldProps['size'];
  variant?: TextFieldProps['variant'];
  fullWidth?: boolean;
  validation?: ControllerProps['rules'];
  type: 'datetime-local' | 'date' | 'time';
  disabled?: boolean;
  valueInField?: Dayjs;
  shouldDisableDate?: (day: Dayjs) => boolean;
  sx?: CSSProperties;
  required?: boolean;
};

function DateTimePickerTranslated<T>({
  name,
  control,
  inputProps,
  size = 'medium',
  variant = 'standard',
  fullWidth = false,
  validation = {},
  disabled = false,
  valueInField,
  shouldDisableDate = () => false,
  required = false,
  ...rest
}: DatePickerTranslatedProps<T>) {
  const { i18n, t } = useTranslation('global');
  const currentLang = i18n.language;

  return (
    <LocalizationProvider
      adapterLocale={currentLang}
      dateAdapter={AdapterDayjs}
    >
      <Controller
        control={control}
        disabled={disabled}
        name={name}
        render={({
          field: { value, onChange, onBlur },
          fieldState: { invalid, error }
        }) => (
          <DateTimePicker
            disableOpenPicker={disabled}
            inputFormat={
              currentLang === 'es' ? 'DD/MM/YYYY HH:mm' : 'MM/DD/YYYY HH:mm'
            }
            onChange={onChange}
            renderInput={(params) => (
              <TextField
                {...params}
                disabled={disabled}
                error={invalid}
                fullWidth={fullWidth}
                helperText={error ? error.message : params.helperText}
                inputProps={{
                  ...params.inputProps,
                  ...inputProps,
                  // readOnly: { disabled },
                  placeholder:
                    currentLang === 'es'
                      ? 'dd/mm/aaaa hh:mm'
                      : 'dd/mm/yyyy hh:mm'
                }}
                onBlur={onBlur}
                size={size}
                variant={variant}
                {...rest}
              />
            )}
            shouldDisableDate={shouldDisableDate}
            value={value || valueInField || null}
          />
        )}
        rules={{
          required: required ? t('common.commonMessages.required') : false,
          ...validation
        }}
      />
    </LocalizationProvider>
  );
}

export default DateTimePickerTranslated;
